import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn } from 'src/utils/utils';

import { Icon } from './icon';
import { Spinner, type SpinnerProps } from './spinner';
import { Typography } from './typography';

const buttonVariants = cva(
  'tw:inline-flex tw:justify-center tw:items-center tw:gap-s tw:rounded-full tw:w-max tw:font-medium tw:transition-colors tw:duration-200 tw:ease-in-out',
  {
    variants: {
      size: {
        medium: 'tw:py-s tw:px-l',
        small: 'tw:py-s tw:px-m'
      },
      hierarchy: {
        primary: `
          tw:bg-btn-brand
          tw:text-btn-primary-fg 
             
          tw:active:bg-btn-brand-pressed
          
          tw:disabled:text-btn-fg-disabled
          tw:disabled:bg-btn-bg-disabled
        `,
        secondary: `
          tw:bg-btn-secondary-bg
          tw:outline
          tw:-outline-offset-1
          tw:outline-solid
          tw:outline-btn-secondary-border
          tw:text-btn-secondary-fg

          tw:active:bg-btn-secondary-bg-pressed
          tw:active:text-btn-secondary-fg-pressed
          tw:active:outline-btn-secondary-border-pressed

          tw:disabled:text-btn-fg-disabled
          tw:disabled:bg-btn-bg-disabled
          tw:disabled:outline-btn-border-disabled
        `,
        ghost: `
          tw:text-btn-tertiary-fg    

          tw:active:text-btn-tertiary-fg-pressed
          tw:active:bg-btn-tertiary-bg-pressed
          
          tw:disabled:text-btn-fg-disabled
          tw:disabled:bg-btn-bg-disabled
        `,
        destructive: `
          tw:outline
          tw:-outline-offset-1
          tw:outline-solid
          tw:outline-btn-destructive-border
          tw:text-btn-destructive-fg
          tw:bg-btn-destructive-bg

          tw:active:text-btn-destructive-fg-pressed
          tw:active:bg-btn-destructive-bg-pressed
          tw:active:outline-btn-destructive-border-pressed

          tw:disabled:text-btn-fg-disabled
          tw:disabled:bg-btn-bg-disabled
          tw:disabled:outline-btn-border-disabled
        `,
        link: `
          tw:text-btn-link-fg
          tw:active:text-btn-link-fg-pressed
          tw:disabled:text-btn-fg-disabled

          tw:p-none
        `,
        linkGray: `
          tw:text-btn-link-gray-fg
          tw:active:text-btn-link-gray-fg-pressed
          tw:disabled:text-btn-fg-disabled

          tw:p-none
        `,
        linkGrayLight: `
          tw:text-btn-link-gray-light-fg
          tw:active:text-btn-link-gray-light-fg-pressed
          tw:disabled:text-btn-fg-disabled

          tw:p-none
        `,
        linkDestructive: `
          tw:text-btn-link-destructive-fg
          tw:active:text-btn-link-destructive-fg-pressed
          tw:disabled:text-btn-fg-disabled

          tw:p-none
        `
      },
      category: {
        default: '',
        rounded: 'tw:rounded-full tw:p-s'
      }
    },
    defaultVariants: {
      size: 'medium',
      hierarchy: 'primary',
      category: 'default'
    }
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /**
   * Text content for the button.
   * If children are provided, this prop is ignored.
   * If iconOnly is true, text is not rendered.
   */
  text?: string;
  /**
   * Icon to display on the left side.
   * Always renders when provided, regardless of iconOnly.
   *
   * @example
   * import { ReactComponent as SaveIcon } from 'src/assets/save.svg';
   * <Button text="Save" leftIcon={SaveIcon} />
   */
  leftIcon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  leftComp?: React.ReactNode;
  /**
   * Icon to display on the right side.
   * Always renders when provided, regardless of iconOnly.
   *
   * @example
   * import { ReactComponent as ArrowIcon } from 'src/assets/arrow.svg';
   * <Button text="Next" rightIcon={ArrowIcon} />
   */
  rightIcon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  /**
   * When true, only icons are rendered (text is hidden).
   * Use with leftIcon and/or rightIcon.
   *
   * @example
   * import { ReactComponent as CloseIcon } from 'src/assets/close.svg';
   * <Button leftIcon={CloseIcon} iconOnly />
   *
   * @default false
   */
  iconOnly?: boolean;
  /**
   * When true, shows a loading spinner.
   * The button is also disabled during loading.
   *
   * @example
   * <Button text="Save" loading />
   *
   * @default false
   */
  loading?: boolean;
  /**
   * Text to display while loading.
   * If not provided, the original text is shown (unless hideTextWhileLoading is true).
   *
   * @example
   * <Button text="Save" loading loadingText="Saving..." />
   */
  loadingText?: string;
  /**
   * When true, hides the text while loading (shows spinner only).
   *
   * @example
   * <Button text="Save" loading hideTextWhileLoading />
   *
   * @default false
   */
  hideTextWhileLoading?: boolean;
  /**
   * Variant of the spinner to use.
   *
   * @default 'line-spinner'
   */
  spinnerVariant?: SpinnerProps['variant'];
  /**
   * Optional color class for the loading spinner (e.g. 'tw:text-btn-primary-fg').
   * When not provided, color is derived from hierarchy.
   */
  spinnerColorClass?: string;
  /**
   * When true, it shows the active state of button.
   *
   * @example
   * <Button active=true />
   *
   * @default false
   */
  active?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      hierarchy,
      category,
      size,
      text,
      leftIcon,
      loading = false,
      loadingText,
      hideTextWhileLoading = false,
      spinnerVariant = 'line-spinner',
      spinnerColorClass,
      asChild = false,
      children,
      disabled,
      active,
      leftComp,
      rightIcon,
      iconOnly = false,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';

    // Determine text color based on button hierarchy
    const getActiveColorClasses = () => {
      if (active) {
        switch (hierarchy) {
          case 'primary':
            return 'tw:bg-btn-brand-pressed';
          case 'secondary':
            return 'tw:bg-btn-secondary-bg-pressed tw:text-btn-secondary-fg-pressed tw:outline-btn-secondary-border-pressed';
          case 'ghost':
            return 'tw:text-btn-tertiary-fg-pressed tw:bg-btn-tertiary-bg-pressed';
          case 'destructive':
            return 'tw:text-btn-destructive-fg-pressed tw:bg-btn-destructive-bg-pressed tw:outline-btn-destructive-border-pressed';
          case 'link':
            return 'tw:text-btn-link-fg-pressed';
          case 'linkGray':
            return 'tw:text-btn-link-gray-fg-pressed';
          case 'linkGrayLight':
            return 'tw:text-btn-link-gray-light-fg-pressed';
          case 'linkDestructive':
            return 'tw:text-btn-link-destructive-fg-pressed';
          default:
            return 'tw:bg-btn-brand-pressed';
        }
      } else {
        switch (hierarchy) {
          case 'primary':
            return 'tw:hover:bg-btn-brand-hover';
          case 'secondary':
            return 'tw:hover:bg-btn-secondary-bg-hover tw:hover:text-btn-secondary-fg-hover tw:hover:outline-btn-secondary-border-hover';
          case 'ghost':
            return 'tw:hover:text-btn-tertiary-fg-hover tw:hover:bg-btn-tertiary-bg-hover';
          case 'destructive':
            return 'tw:hover:text-btn-destructive-fg-hover tw:hover:bg-btn-destructive-bg-hover tw:hover:outline-btn-destructive-border-hover';
          case 'link':
            return 'tw:hover:text-btn-link-fg-hover';
          case 'linkGray':
            return 'tw:hover:text-btn-link-gray-fg-hover';
          case 'linkGrayLight':
            return 'tw:hover:text-btn-link-gray-light-fg-hover';
          case 'linkDestructive':
            return 'tw:hover:text-btn-link-destructive-fg-hover';
          default:
            return 'tw:hover:bg-btn-brand-hover';
        }
      }
    };

    const getCursorClass = () => {
      if (disabled || loading) {
        return 'tw:cursor-not-allowed';
      } else if (active) {
        return 'tw:cursor-default';
      }
      return 'tw:cursor-pointer';
    };

    // Determine text size based on button size
    const getTextSize = () => {
      switch (size) {
        case 'medium':
          return 'h5';
        case 'small':
          return 'h6';
        default:
          return 'h5';
      }
    };

    // Determine icon size class based on button size (using design token size classes)
    const getIconSizeClass = (): string => {
      switch (size) {
        case 'medium':
        case 'small':
          return 'tw:h-m tw:w-m';
        default:
          return 'tw:h-m tw:w-m';
      }
    };

    const getButtonPaddingClass = (): string => {
      if (iconOnly) {
        switch (size) {
          case 'medium':
            return 'tw:py-s-m tw:px-s-m';
          case 'small':
            return 'tw:py-s tw:px-s';
          default:
            return 'tw:py-s-m tw:px-s-m';
        }
      } else {
        return '';
      }
    };

    // Determine spinner color based on button hierarchy
    const getSpinnerColorClass = (): string => {
      switch (hierarchy) {
        case 'primary':
          return 'tw:text-text-over-brand';
        case 'secondary':
        case 'ghost':
        case 'destructive':
        case 'link':
        default:
          return 'tw:text-icon-brand';
      }
    };

    // Determine the display text during loading
    const displayText = loading
      ? hideTextWhileLoading
        ? undefined
        : loadingText ?? text
      : text;

    // Determine if we should show text
    const shouldShowText = !iconOnly && displayText;

    // Button is disabled when loading or explicitly disabled
    const isDisabled = loading || disabled;

    // Render button with children or text with optional icons
    return (
      <Comp
        className={cn(
          buttonVariants({ hierarchy, size, category }),
          getActiveColorClasses(),
          getCursorClass(),
          getButtonPaddingClass(),
          className
        )}
        ref={ref}
        disabled={isDisabled}
        aria-busy={loading}
        {...props}
      >
        {children || (
          <>
            {loading ? (
              <Spinner
                variant={spinnerVariant}
                size="xs"
                showText={false}
                colorClass={spinnerColorClass ?? getSpinnerColorClass()}
              />
            ) : (
              leftComp ||
              (leftIcon && (
                <Icon icon={leftIcon} sizeClass={getIconSizeClass()} />
              ))
            )}
            {shouldShowText ? (
              <Typography variant={getTextSize()}>{displayText}</Typography>
            ) : null}
            {!loading && rightIcon ? (
              <Icon icon={rightIcon} sizeClass={getIconSizeClass()} />
            ) : null}
          </>
        )}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
